/**
 * Commander deck validator.
 *
 * Pure function — no I/O, no side effects. Checks deck size, singleton rule,
 * color identity, commander legality, and card bans.
 *
 * Moved from packages/client/src/lib/validation.ts to shared so that the
 * server can call it when producing validation warnings during MCP imports.
 * A re-export shim at the original client path preserves backwards compat.
 */
import { isBasicLand, isLegalCommander } from './card-helpers.js';
import { isWithinColorIdentity } from './color-identity.js';
export function validateDeck(entries, colorIdentity) {
    if (entries.length === 0)
        return { valid: true, violations: [] };
    const violations = [];
    const totalCards = entries.reduce((sum, e) => sum + e.quantity, 0);
    // ── 1. Deck size ──────────────────────────────────────────────────────────
    if (totalCards !== 100) {
        violations.push({
            type: 'deck_size',
            severity: totalCards > 100 ? 'error' : 'warning',
            message: totalCards > 100
                ? `Deck has ${totalCards} cards — must be exactly 100.`
                : `Deck has ${totalCards}/100 cards.`,
        });
    }
    // ── 2. Invalid commanders ─────────────────────────────────────────────────
    const commanders = entries.filter((e) => e.isCommander);
    if (commanders.length === 0) {
        violations.push({
            type: 'invalid_commander',
            severity: 'error',
            message: 'Deck has no designated commander.',
        });
    }
    for (const entry of commanders) {
        if (!isLegalCommander(entry.card)) {
            violations.push({
                type: 'invalid_commander',
                severity: 'error',
                message: `${entry.card.name} cannot be your commander (not a legendary creature or designatable card).`,
                cardName: entry.card.name,
            });
        }
    }
    // ── 3. Singleton ──────────────────────────────────────────────────────────
    for (const entry of entries) {
        if (isBasicLand(entry.card))
            continue;
        if (entry.quantity > 1) {
            violations.push({
                type: 'singleton',
                severity: 'error',
                message: `${entry.card.name} appears ${entry.quantity} times — Commander is singleton format.`,
                cardName: entry.card.name,
            });
        }
    }
    // ── 4. Color identity ─────────────────────────────────────────────────────
    if (colorIdentity.length > 0 || commanders.length > 0) {
        const nonCommanders = entries.filter((e) => !e.isCommander);
        for (const entry of nonCommanders) {
            if (!isWithinColorIdentity(entry.card.colorIdentity, colorIdentity)) {
                violations.push({
                    type: 'color_identity',
                    severity: 'error',
                    message: `${entry.card.name} is outside your commander's color identity.`,
                    cardName: entry.card.name,
                });
            }
        }
    }
    // ── 5. Banned cards ───────────────────────────────────────────────────────
    for (const entry of entries) {
        if (entry.card.legalities.commander === 'banned') {
            violations.push({
                type: 'banned',
                severity: 'error',
                message: `${entry.card.name} is banned in Commander.`,
                cardName: entry.card.name,
            });
        }
    }
    return {
        valid: violations.filter((v) => v.severity === 'error').length === 0,
        violations,
    };
}
//# sourceMappingURL=validate-deck.js.map